import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useContent } from "@/lib/useContent";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/start-here", label: "Start" },
    { to: "/about", label: "About" },
    { to: "/gallery", label: "Gallery" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/nc500", label: "NC500" },
    { to: "/map", label: "Map" },
    { to: "/spotlight", label: "Spotlight" },
    { to: "/pilots-log", label: "Log" },
    { to: "/book", label: "Book" },
    { to: "/book#book-form-step-1", label: "Calendar" },
    { to: "/safety", label: "Safety" },
    { to: "/subscription", label: "Subscribe" },
    { to: "/things", label: "Do" },
    { to: "/blog", label: "Journal" },
    { to: "/commercial-enquiries", label: "Commercial" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { data: logo } = useContent("site_logo", { data_url: "" });

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <header className="fixed top-0 inset-x-0 z-50 glass-nav" data-testid="site-navbar">
            <div className="w-full text-center py-2 border-b border-[#0055A4]/40" style={{background: "rgba(0, 20, 40, 0.55)"}} data-testid="motto-strip">
                <span style={{
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 800,
                    color: "#3b8fd9",
                    letterSpacing: "0.18em",
                    fontSize: "clamp(1rem, 2.3vw, 1.6rem)",
                    textShadow: "0 0 24px rgba(59, 143, 217, 0.55), 0 0 4px rgba(0, 85, 164, 0.6)",
                }}>
                    GET IT DID!
                </span>
            </div>
            <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
                <Link to="/" className="flex items-center gap-3 group whitespace-nowrap shrink-0" data-testid="nav-brand">
                    {logo?.data_url ? (
                        <>
                            <img src={logo.data_url} alt="DTScotslad" className="h-10 w-10 object-contain" data-testid="nav-logo" />
                            <span className="font-serif text-base tracking-wide leading-none">DTScotslad</span>
                        </>
                    ) : (
                        <>
                            <div className="w-8 h-8 border border-[var(--sb-brand)] flex items-center justify-center group-hover:border-[var(--sb-text)] transition-colors">
                                <span className="font-serif text-lg leading-none text-[var(--sb-brand)] group-hover:text-[var(--sb-text)] transition-colors">S</span>
                            </div>
                            <span className="font-serif text-base tracking-wide leading-none">DTScotslad</span>
                        </>
                    )}
                </Link>

                {/* Central tagline — fills the space between the brand and the
                    auth cluster, giving the header a masthead feel. Auto-hides
                    on phones where every pixel matters. */}
                <div
                    className="hidden md:flex flex-1 items-center gap-4 min-w-0 px-6"
                    data-testid="nav-tagline-wrap"
                >
                    <span className="h-px flex-1 bg-[var(--sb-border)]" aria-hidden />
                    <span
                        className="font-serif italic tracking-wide whitespace-nowrap"
                        style={{ fontSize: "clamp(1rem, 2.3vw, 1.6rem)", color: "#ffffff", letterSpacing: "0.06em" }}
                        data-testid="nav-tagline"
                    >
                        A Drone Pilot&rsquo;s Diary
                    </span>
                    <span className="h-px flex-1 bg-[var(--sb-border)]" aria-hidden />
                </div>

                {/* Desktop inline nav removed — every link now lives inside
                    the dropdown menu (opened by the hamburger button below)
                    so the header stays clean and the Join CTA is unmissable. */}

                {/* Header-right cluster: primary CTA + menu toggle.
                    Always visible from mobile through 4K. */}
                <div className="flex items-center gap-3 shrink-0">
                    {user ? (
                        <>
                            <Link
                                to="/dashboard"
                                className="hidden sm:inline-flex items-center gap-2 text-sm text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] transition-colors whitespace-nowrap"
                                data-testid="nav-dashboard"
                            >
                                <User size={16} /> {user.name?.split(" ")[0] || "Account"}
                            </Link>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                className="hidden sm:inline-block text-sm text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] transition-colors whitespace-nowrap"
                                data-testid="nav-login"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/register"
                                className="btn-primary text-sm whitespace-nowrap"
                                data-testid="nav-register"
                                style={{ padding: "0.5rem 1.1rem" }}
                            >
                                Join
                            </Link>
                        </>
                    )}
                    <button
                        onClick={() => setOpen(!open)}
                        className="text-[var(--sb-text)] shrink-0 ml-1 hover:text-[var(--sb-brand)] transition-colors"
                        data-testid="nav-toggle"
                        aria-label={open ? "Close menu" : "Open menu"}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="border-t border-[var(--sb-border)] bg-[var(--sb-bg)]/95 backdrop-blur-md" data-testid="mobile-nav">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex flex-col gap-4">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                className="text-sm tracking-wider uppercase text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] py-2"
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                            >
                                {n.label}
                            </Link>
                        ))}
                        <div className="divider my-6" />
                        {user ? (
                            <>
                                {user.role === "admin" && (
                                    <Link to="/admin" onClick={() => setOpen(false)} className="text-sm uppercase tracking-wider text-[var(--sb-brand)] py-2">Studio (Admin)</Link>
                                )}
                                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm uppercase tracking-wider text-[var(--sb-text)] py-2">My Account</Link>
                                <button onClick={() => { logout(); setOpen(false); }} className="text-left text-sm uppercase tracking-wider text-[var(--sb-text-muted)] py-2">Sign out</button>
                            </>
                        ) : (
                            <div className="flex flex-col gap-3 pt-2">
                                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline text-sm w-full text-center justify-center" style={{padding: "0.75rem 1rem"}} data-testid="mobile-nav-signin">Sign in</Link>
                                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary text-sm w-full text-center justify-center" style={{padding: "0.75rem 1rem"}} data-testid="mobile-nav-join">Join</Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
